import { Trans, useTranslation } from 'react-i18next';
import { OmsOrderDetail, OmsOrderPayment } from 'types/oms';
import LOCALS from 'commons/locals';
import {
  Button,
  Form,
  Input,
  Modal,
  Space,
  Table,
  message,
  Image,
  Card,
  Tag,
} from 'antd';
import { OFFLINE_PAY_TYPE_LIST } from 'commons';
import { useCallback, useMemo, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import {
  ORDER_PAYMENT_STATUS_MAP,
  getOrderPaymentStatusLabel,
  getOrderPaymentStatusTagColor,
} from 'constant/oms-order-payment-status';
import DualTimezoneTime from 'components/dual-timezone-time';
import { omsOrderOfflinePayAccept, omsOrderOfflinePayReject } from 'apis/oms';
import PrivateReceiptImage from 'components/private-receipt-image';
import { CreditCard } from 'lucide-react';
import { ORDER_STATUS_MAP } from 'constant/oms-order-status';

type OfflinePaymentRecordProps = {
  omsOrderDetail: OmsOrderDetail;
  stockPlace?: string;
};

type OfflinePayConfirmType = 'confirm' | 'reject';

const OfflinePaymentRecord = ({
  omsOrderDetail: { payList, status, id },
  stockPlace,
}: OfflinePaymentRecordProps) => {
  const { i18n } = useTranslation();
  const [offlinePayConfirmType, setOfflinePayConfirmType] =
    useState<OfflinePayConfirmType>('confirm');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalForm] = Form.useForm();

  const offlinePayList = payList
    ? payList.filter(({ payType }) => {
        return OFFLINE_PAY_TYPE_LIST.includes(payType);
      })
    : [];

  const onModalOk = useCallback(async () => {
    const data = await modalForm.validateFields();

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        if (offlinePayConfirmType === 'confirm') {
          await omsOrderOfflinePayAccept({
            orderId: id,
            ...data,
          });
        }

        if (offlinePayConfirmType === 'reject') {
          await omsOrderOfflinePayReject({
            orderId: id,
            ...data,
          });
        }

        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [i18n, id, modalForm, offlinePayConfirmType]);

  const columns: ColumnsType<OmsOrderPayment> = useMemo(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.payment_channel}></Trans>,
        dataIndex: 'payType',
        key: 'payType',
      },
      {
        title: <Trans i18nKey={LOCALS.pay_amount}></Trans>,
        dataIndex: 'payAmount',
        key: 'payAmount',
        render: (payAmount: OmsOrderPayment['payAmount']) => {
          return payAmount ? payAmount.toLocaleString() : '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.currency}></Trans>,
        dataIndex: 'payCurrency',
        key: 'payCurrency',
        render: (payCurrency: OmsOrderPayment['payCurrency']) => {
          return payCurrency || '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_status}></Trans>,
        dataIndex: 'payStatus',
        key: 'payStatus',
        render: (payStatus: OmsOrderPayment['payStatus']) => {
          if (!payStatus) return '-';
          const text = getOrderPaymentStatusLabel(payStatus);
          return (
            <Tag color={getOrderPaymentStatusTagColor(payStatus)}>{text}</Tag>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_time}></Trans>,
        dataIndex: 'payTime',
        key: 'payTime',
        render: (payTime: OmsOrderPayment['payTime']) => {
          return <DualTimezoneTime time={payTime} stockPlace={stockPlace} />;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_voucher}></Trans>,
        dataIndex: 'memberTransferVoucher',
        key: 'memberTransferVoucher',
        render: (
          memberTransferVoucher: OmsOrderPayment['memberTransferVoucher'],
        ) => {
          if (!memberTransferVoucher) return '-';
          if (/^https?:\/\//i.test(memberTransferVoucher)) {
            return <Image width={100} src={memberTransferVoucher} />;
          }
          return (
            <PrivateReceiptImage imageKey={memberTransferVoucher} width={100} />
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_note}></Trans>,
        dataIndex: 'memberTransferDesc',
        key: 'memberTransferDesc',
        render: (memberTransferDesc: OmsOrderPayment['memberTransferDesc']) => {
          return memberTransferDesc || '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.confirmor}></Trans>,
        dataIndex: 'confirmUser',
        key: 'confirmUser',
        render: (confirmUser: OmsOrderPayment['confirmUser']) => {
          return confirmUser || '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.confirm_time}></Trans>,
        dataIndex: 'confirmTime',
        key: 'confirmTime',
        render: (confirmTime: OmsOrderPayment['confirmTime']) => {
          return (
            <DualTimezoneTime time={confirmTime} stockPlace={stockPlace} />
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.options}></Trans>,
        key: 'options',
        fixed: 'right',
        render: ({ payStatus }: OmsOrderPayment) => {
          if (payStatus !== ORDER_PAYMENT_STATUS_MAP.PENDING_APPROVAL)
            return '-';
          if (status !== ORDER_STATUS_MAP.TO_BE_PAID) return '-';

          return (
            <Space>
              <Button
                onClick={() => {
                  setOfflinePayConfirmType('confirm');
                  setModalOpen(true);
                }}
              >
                <Trans i18nKey={LOCALS.confirm} />
              </Button>
              <Button
                onClick={() => {
                  setOfflinePayConfirmType('reject');
                  setModalOpen(true);
                }}
              >
                <Trans i18nKey={LOCALS.reject} />
              </Button>
            </Space>
          );
        },
      },
    ];
  }, [status, stockPlace]);

  if (!offlinePayList.length) {
    return null;
  }

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <CreditCard className="w-5 h-5" />
        <Trans i18nKey={LOCALS.offline_payment_record} />
      </div>

      <Table
        size="small"
        rowKey={'id'}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        pagination={false}
        columns={columns}
        dataSource={offlinePayList}
        bordered
      ></Table>

      <Modal
        open={modalOpen}
        title={<Trans i18nKey={LOCALS.offline_payment_record_confirm} />}
        onCancel={() => {
          setModalOpen(false);
        }}
        onOk={onModalOk}
      >
        <Form form={modalForm}>
          <Form.Item
            label={<Trans i18nKey={LOCALS.remark} />}
            name="note"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input.TextArea placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default OfflinePaymentRecord;
